import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit, 
  Power,
  PowerOff,
  CheckCircle, 
  XCircle, 
  Users, 
  DollarSign, 
  Target, 
  Calendar,
  FileText,
  Eye,
  Briefcase,
  Crown,
  Star,
  Zap,
  BarChart3,
  Search,
  Award,
  List
} from 'lucide-react';
import { 
  Chip,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import adminApi from '../../api/admin';
import Button from '../../components/common/Button';
import { toast } from 'react-toastify';
import { formatDateForDisplay } from '../../utils/helpers';
import SubscriptionPlanModal from '../../components/admin/SubscriptionPlanModal';
import ActivateDeactivatePlanDialog from '../../components/admin/ActivateDeactivatePlanDialog';

const AdminSubscriptionPlanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusAction, setStatusAction] = useState('');
  const [reason, setReason] = useState('');

  const {
    data: plan,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ["subscription-plan-details", id],
    queryFn: async () => {
      const res = await adminApi.getSubscriptionPlanDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: ({ id, planData }) => adminApi.updateSubscriptionPlan(id, planData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setIsModalOpen(false);
      setInitialData(null);
      toast.success('Plan updated successfully!');
      refetch()
    },
    onError: (error) => {
      console.error('Failed to update plan:', error);
      toast.error('Failed to update plan');
    }
  });

  // Activate/Deactivate plan mutation
  const updatePlanStatusMutation = useMutation({
    mutationFn: ({ id, action, reason }) => adminApi.updatePlanStatus(id, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plan-details', id] });
      setStatusDialogOpen(false);
      setReason('');
      toast.success(`Plan ${statusAction === 'activate' ? 'activated' : 'deactivated'} successfully!`);
    },
    onError: (error) => {
      console.error('Failed to update plan status:', error);
      toast.error(`Failed to ${statusAction} plan`);
    }
  });

  const handleEdit = () => {
    setInitialData(plan);
    setIsModalOpen(true);
  };

  const handleStatusChange = (action) => {
    setStatusAction(action);
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = (action, reason) => {
    if (plan) {
      updatePlanStatusMutation.mutate({ 
        id: plan.id, 
        action, 
        reason 
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setInitialData(null);
  };

  const handleSubmitPlan = (planData) => {
    if (initialData) {
      updatePlanMutation.mutate({ id: initialData.id, planData });
    }
  };

  const handleBack = () => {
    navigate('/admin/subscription-plans');
  };

  // Parse features string into an array of features
  const parseFeatures = (featuresString) => {
    if (!featuresString || typeof featuresString !== 'string') {
      return [];
    }
    
    // Split by commas and clean up each feature
    return featuresString
      .split(',')
      .map(feature => feature.trim())
      .filter(feature => feature.length > 0);
  };

  // Get icon for feature based on keywords
  const getFeatureIcon = (feature) => {
    const featureLower = feature.toLowerCase();
    
    if (featureLower.includes('job') || featureLower.includes('posting')) {
      return <Briefcase className="w-4 h-4" />;
    }
    if (featureLower.includes('profile') || featureLower.includes('view')) {
      return <Eye className="w-4 h-4" />;
    }
    if (featureLower.includes('application') || featureLower.includes('cv')) {
      return <FileText className="w-4 h-4" />;
    }
    if (featureLower.includes('analytics') || featureLower.includes('report')) {
      return <BarChart3 className="w-4 h-4" />;
    }
    if (featureLower.includes('search')) {
      return <Search className="w-4 h-4" />;
    }
    if (featureLower.includes('support') || featureLower.includes('priority')) {
      return <Award className="w-4 h-4" />;
    }
    
    return <CheckCircle className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading subscription plan: {error?.message}</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Plans
          </Button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Subscription plan not found</p>
          <Button 
            onClick={handleBack}
            variant="outline" 
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Plans
          </Button>
        </div>
      </div>
    );
  }

  const features = parseFeatures(plan.features);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={handleBack}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Plan Details</h1>
            <p className="text-secondary-600">View and manage subscription plan information</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => handleStatusChange(plan.is_active ? 'deactivate' : 'activate')}
            icon={plan.is_active ? <PowerOff className="w-4 h-4 mr-2" /> : <Power className="w-4 h-4 mr-2" />}
            disabled={updatePlanStatusMutation.isLoading}
            color={plan.is_active ? 'warning' : 'success'}
          >
            {plan.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            onClick={handleEdit}
            icon={<Edit className="w-4 h-4 mr-2" />}
          >
            Edit Plan
          </Button>
        </div>
      </div>

      {/* Plan Overview */}
      <Grid container spacing={3} className="mb-6">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    {parseFloat(plan.price) > 50 ? (
                      <Crown className="w-8 h-8 text-white" />
                    ) : (
                      <Star className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h2 className="text-2xl font-bold text-secondary-900">{plan.name}</h2>
                      <Chip 
                        label={plan.is_active ? 'Active' : 'Inactive'} 
                        color={plan.is_active ? 'success' : 'default'}
                        icon={plan.is_active ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      />
                    </div>
                    <p className="text-secondary-600 text-lg">{plan.description}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    {/* <DollarSign className="w-6 h-6 text-green-600" /> */}
                    <span className="text-3xl font-bold text-secondary-900">{plan.price}</span>
                  </div>
                  <div className="text-sm text-secondary-600">
                    {plan.currency} • {plan.target_role === 'professional' ? 'For Professionals' : 'For Recruiters'}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {plan.job_application_limit > 0 ? plan.job_application_limit : '∞'}
                  </div>
                  <div className="text-sm text-secondary-600">Applications</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {plan.job_posting_limit > 0 ? plan.job_posting_limit : '∞'}
                  </div>
                  <div className="text-sm text-secondary-600">Job Postings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {plan.profile_view_limit > 0 ? plan.profile_view_limit : '∞'}
                  </div>
                  <div className="text-sm text-secondary-600">Profile Views</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs:12, lg: 4}}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">Plan Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Target Role</span>
                  <Chip 
                    label={plan.target_role} 
                    color={plan.target_role === 'professional' ? 'primary' : 'secondary'}
                    size="small"
                    icon={<Target className="w-3 h-3" />}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Currency</span>
                  <span className="font-medium">{plan.currency}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Status</span>
                  <Chip 
                    label={plan.is_active ? 'Active' : 'Inactive'} 
                    color={plan.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </div>
                <Divider />
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Created</span>
                  <span className="font-medium text-sm">
                    {formatDateForDisplay(plan.created_at)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-secondary-600">Last Updated</span>
                  <span className="font-medium text-sm">
                    {formatDateForDisplay(plan.updated_at)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Section */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <h3 className="text-xl font-semibold mb-6 text-secondary-900 flex items-center">
            <List className="w-5 h-5 mr-2" />
            Plan Features
          </h3>
          {features.length > 0 ? (
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-shrink-0 text-blue-600">
                      {getFeatureIcon(feature)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-secondary-900 text-sm">
                        {feature}
                      </div>
                    </div>
                  </div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <div className="text-center py-8 text-secondary-500">
              <Zap className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No features configured for this plan</p>
              {/* <p className="text-sm mt-2">Features will be displayed as a comma-separated list</p> */}
            </div>
          )}
          
          {/* {plan.features && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <Typography variant="body2" className="text-gray-600 mb-2">
                Raw features data:
              </Typography>
              <pre className="text-sm bg-white p-2 rounded border block">
                {plan.features}
              </pre>
            </div>
          )} */}
        </CardContent>
      </Card>

      {/* Edit Plan Modal */}
      <SubscriptionPlanModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPlan}
        isLoading={updatePlanMutation.isLoading}
        title="Update Plan"
        submitButtonText="Update Plan"
        initialData={initialData}
      />

      {/* Activate/Deactivate Dialog */}
      <ActivateDeactivatePlanDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        onConfirm={handleStatusConfirm}
        isLoading={updatePlanStatusMutation.isLoading}
        plan={plan}
        action={statusAction}
        reason={reason}
        onReasonChange={setReason}
      />
    </div>
  );
}

export default AdminSubscriptionPlanDetails;